import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalBody,
  Label,
  Input,
  Button,
  FormFeedback,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import useAwardingApi from '../hooks/useAwardingApi';
import { unwrapAwardingCollection } from '../utils/awardingHelpers';

const MAX_FILE_SIZE_MB = 10;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const UploadDealRecapModal = ({
  isOpen,
  toggle,
  onSubmit,
  isSubmitting,
  defaultValues = {},
}) => {
  const { getRegisteredSuppliers } = useAwardingApi();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [companyMitraId, setCompanyMitraId] = useState('');
  const [picName, setPicName] = useState('');
  const [tradingEmail, setTradingEmail] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;

    setFile(null);
    setCompanyMitraId(defaultValues.companyMitraId || '');
    setPicName(defaultValues.picName || '');
    setTradingEmail(defaultValues.tradingEmail || '');
    setErrors({});

    let cancelled = false;
    const loadSuppliers = async () => {
      setIsLoadingSuppliers(true);
      try {
        const result = await getRegisteredSuppliers();
        if (!cancelled) {
          setSuppliers(unwrapAwardingCollection(result));
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error.message || 'Failed to load registered supplier');
          setSuppliers([]);
        }
      } finally {
        if (!cancelled) setIsLoadingSuppliers(false);
      }
    };

    loadSuppliers();
    return () => {
      cancelled = true;
    };
    // defaultValues sengaja tidak masuk dependency agar form tidak ter-reset saat detail refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, getRegisteredSuppliers]);

  const handleFileChange = event => {
    const selected = event.target.files?.[0];
    if (!selected) {
      setFile(null);
      return;
    }

    const isPdf =
      selected.type === 'application/pdf' ||
      selected.name.toLowerCase().endsWith('.pdf');

    if (!isPdf) {
      setErrors(prev => ({ ...prev, file: 'File must be a PDF' }));
      setFile(null);
      event.target.value = '';
      return;
    }

    if (selected.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        file: `File size must not exceed ${MAX_FILE_SIZE_MB} MB`,
      }));
      setFile(null);
      event.target.value = '';
      return;
    }

    setErrors(prev => ({ ...prev, file: undefined }));
    setFile(selected);
  };

  const handleSupplierChange = event => {
    const value = event.target.value;
    setCompanyMitraId(value);
    setErrors(prev => ({ ...prev, companyMitraId: undefined }));

    const supplier = suppliers.find(item => item.id === value);
    if (supplier?.email && !tradingEmail) {
      setTradingEmail(supplier.email);
    }
  };

  const handleSubmit = () => {
    const nextErrors = {};
    if (!file) nextErrors.file = 'File is required';
    if (!companyMitraId) {
      nextErrors.companyMitraId = 'Registered supplier is required';
    }
    if (!picName.trim()) nextErrors.picName = 'Name PIC is required';
    if (!tradingEmail.trim()) {
      nextErrors.tradingEmail = 'Email trading is required';
    } else if (!EMAIL_PATTERN.test(tradingEmail.trim())) {
      nextErrors.tradingEmail = 'Email trading is not valid';
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    onSubmit({
      file,
      companyMitraId,
      picName: picName.trim(),
      tradingEmail: tradingEmail.trim(),
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalBody className='p-4'>
        <div className='d-flex justify-content-between align-items-start mb-4'>
          <h5 className='fw-bold mb-0'>Upload Deal Recap</h5>
          <button
            type='button'
            className='btn-close'
            onClick={toggle}
            aria-label='Close'
            disabled={isSubmitting}
          ></button>
        </div>

        <div className='mb-3'>
          <Label className='form-label fw-medium'>File</Label>
          <Input
            type='file'
            accept='application/pdf,.pdf'
            innerRef={fileInputRef}
            onChange={handleFileChange}
            invalid={Boolean(errors.file)}
            disabled={isSubmitting}
          />
          <FormFeedback>{errors.file}</FormFeedback>
          <small className='text-muted'>
            PDF only, max {MAX_FILE_SIZE_MB} MB.
          </small>
        </div>

        <div className='mb-3'>
          <Label className='form-label fw-medium'>Send to</Label>
          <Input
            type='select'
            value={companyMitraId}
            onChange={handleSupplierChange}
            invalid={Boolean(errors.companyMitraId)}
            disabled={isSubmitting || isLoadingSuppliers}
          >
            <option value=''>
              {isLoadingSuppliers
                ? 'Loading registered supplier...'
                : 'Choose registered supplier'}
            </option>
            {suppliers.map(supplier => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </Input>
          <FormFeedback>{errors.companyMitraId}</FormFeedback>
        </div>

        <div className='mb-3'>
          <Label className='form-label fw-medium'>Name PIC</Label>
          <Input
            type='text'
            placeholder='Enter name of PIC'
            value={picName}
            onChange={e => {
              setPicName(e.target.value);
              setErrors(prev => ({ ...prev, picName: undefined }));
            }}
            invalid={Boolean(errors.picName)}
            disabled={isSubmitting}
          />
          <FormFeedback>{errors.picName}</FormFeedback>
        </div>

        <div className='mb-4'>
          <Label className='form-label fw-medium'>Email Trading</Label>
          <Input
            type='email'
            placeholder='Enter email trading'
            value={tradingEmail}
            onChange={e => {
              setTradingEmail(e.target.value);
              setErrors(prev => ({ ...prev, tradingEmail: undefined }));
            }}
            invalid={Boolean(errors.tradingEmail)}
            disabled={isSubmitting}
          />
          <FormFeedback>{errors.tradingEmail}</FormFeedback>
        </div>

        <div className='d-flex justify-content-end align-items-center gap-2'>
          <Button
            color='link'
            className='text-decoration-none'
            onClick={toggle}
            disabled={isSubmitting}
          >
            Close
          </Button>
          <Button
            color='primary'
            className='px-4'
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner size='sm' className='me-2' />
                Sending...
              </>
            ) : (
              'Upload & Sent File'
            )}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default UploadDealRecapModal;
